
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Gerador, Cliente } from '@/types';

// Function to export data to CSV
export const exportToCSV = (data: any[], filename: string) => {
  // Convert object array to CSV string
  const csvContent = [
    // Header row (keys from first object)
    Object.keys(data[0]).join(','),
    // Data rows
    ...data.map(item => 
      Object.values(item)
        .map(value => 
          typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value
        )
        .join(',')
    )
  ].join('\n');
  
  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Cores para o PDF
const PDF_COLORS = {
  green: '#22c55e',
  blue: '#3b82f6',
  yellow: '#eab308',
  text: '#1f2937',
  lightGray: '#f3f4f6',
  gray: '#9ca3af',
  white: '#ffffff',
};

// Função para exportar elemento HTML como PDF
export const exportToPDF = async (
  elementId: string, 
  fileName: string,
  orientation: 'portrait' | 'landscape' = 'portrait',
  title?: string
) => {
  const element = document.getElementById(elementId);
  if (!element) return false;
  
  // Clone o elemento para manipular sem afetar a UI
  const clone = element.cloneNode(true) as HTMLElement;
  clone.style.padding = '20px';
  clone.style.backgroundColor = '#ffffff';
  
  // Temporariamente adiciona ao DOM para renderizar
  clone.style.position = 'absolute';
  clone.style.left = '-9999px';
  document.body.appendChild(clone);
  
  try {
    const canvas = await html2canvas(clone, {
      scale: 2, // Melhor qualidade
      useCORS: true,
      logging: false,
    });
    
    document.body.removeChild(clone);
    
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation,
      unit: 'mm',
    });
    
    // Adiciona título se fornecido
    if (title) {
      pdf.setFontSize(20);
      pdf.setTextColor(PDF_COLORS.text);
      pdf.text(title, 14, 15);
      pdf.setLineWidth(0.5);
      pdf.setDrawColor(PDF_COLORS.gray);
      pdf.line(14, 20, pdf.internal.pageSize.width - 14, 20);
      pdf.setFontSize(12);
    }
    
    // Calcula as dimensões para manter a proporção
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const ratio = canvas.width / canvas.height;
    
    let imgWidth = pageWidth - 20;
    let imgHeight = imgWidth / ratio;
    
    // Se for muito alto, redimensiona
    if (imgHeight > pageHeight - 40) {
      imgHeight = pageHeight - 40;
      imgWidth = imgHeight * ratio;
    }
    
    // Posiciona a imagem
    const xPos = (pageWidth - imgWidth) / 2;
    const yPos = title ? 30 : 10;
    
    pdf.addImage(imgData, 'PNG', xPos, yPos, imgWidth, imgHeight);
    
    // Adiciona rodapé com data
    const date = new Date().toLocaleDateString('pt-BR');
    pdf.setFontSize(8);
    pdf.setTextColor(PDF_COLORS.gray);
    pdf.text(`Gerado em: ${date}`, 14, pageHeight - 10);
    
    pdf.save(`${fileName}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar PDF:', error);
    document.body.removeChild(clone);
    return false;
  }
};

// Função para gerar PDF de geradores
export const generateGeradoresPDF = (geradores: Gerador[]) => {
  // Cria um elemento temporário para renderizar o conteúdo
  const element = document.createElement('div');
  element.id = 'pdf-temp-geradores';
  element.style.width = '800px';
  element.style.padding = '20px';
  element.style.backgroundColor = '#ffffff';
  element.style.fontFamily = 'Arial, sans-serif';
  
  // Adiciona título e estilo
  const header = document.createElement('div');
  header.innerHTML = `
    <h1 style="font-size: 24px; color: ${PDF_COLORS.green}; margin-bottom: 10px; text-align: center;">
      Relatório de Geradores
    </h1>
    <p style="color: ${PDF_COLORS.gray}; margin-bottom: 20px; text-align: center;">
      Total de ${geradores.length} geradores cadastrados
    </p>
  `;
  element.appendChild(header);

  // Cria elementos para estatísticas
  const estatisticas = document.createElement('div');
  estatisticas.style.display = 'flex';
  estatisticas.style.justifyContent = 'space-between';
  estatisticas.style.marginBottom = '20px';
  
  // Cálculo de estatísticas
  const potenciaTotal = geradores.reduce((sum, g) => sum + g.potenciaInstalada, 0);
  const geracaoTotal = geradores.reduce((sum, g) => sum + g.geracaoEstimada, 0);
  const investimentoTotal = geradores.reduce((sum, g) => sum + g.custoInvestimento, 0);
  
  // Lista de estatísticas
  const estatisticasItems = [
    { label: 'Potência Total', value: `${potenciaTotal.toFixed(2)} kWp`, color: PDF_COLORS.green },
    { label: 'Geração Estimada', value: `${geracaoTotal.toFixed(2)} kWh/mês`, color: PDF_COLORS.yellow },
    { label: 'Investimento Total', value: `R$ ${investimentoTotal.toLocaleString('pt-BR', {minimumFractionDigits: 2})}`, color: PDF_COLORS.blue },
  ];
  
  estatisticasItems.forEach(item => {
    const estatisticaItem = document.createElement('div');
    estatisticaItem.style.padding = '10px';
    estatisticaItem.style.backgroundColor = PDF_COLORS.lightGray;
    estatisticaItem.style.borderRadius = '8px';
    estatisticaItem.style.textAlign = 'center';
    estatisticaItem.style.width = '32%';
    
    estatisticaItem.innerHTML = `
      <h3 style="margin: 0 0 5px 0; color: ${item.color}; font-size: 14px;">${item.label}</h3>
      <p style="margin: 0; font-size: 18px; font-weight: bold;">${item.value}</p>
    `;
    
    estatisticas.appendChild(estatisticaItem);
  });
  
  element.appendChild(estatisticas);
  
  // Tabela de geradores
  const table = document.createElement('table');
  table.style.width = '100%';
  table.style.borderCollapse = 'collapse';
  table.style.marginTop = '20px';
  
  // Cabeçalho da tabela
  const thead = document.createElement('thead');
  thead.innerHTML = `
    <tr style="background-color: ${PDF_COLORS.green}; color: white;">
      <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Nome do Titular</th>
      <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Localização</th>
      <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Concessionária</th>
      <th style="padding: 10px; text-align: right; border: 1px solid #ddd;">Potência (kWp)</th>
      <th style="padding: 10px; text-align: right; border: 1px solid #ddd;">Geração (kWh/mês)</th>
      <th style="padding: 10px; text-align: right; border: 1px solid #ddd;">Investimento (R$)</th>
      <th style="padding: 10px; text-align: right; border: 1px solid #ddd;">Payback (anos)</th>
    </tr>
  `;
  table.appendChild(thead);
  
  // Corpo da tabela
  const tbody = document.createElement('tbody');
  geradores.forEach((gerador, index) => {
    const row = document.createElement('tr');
    row.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb';
    
    row.innerHTML = `
      <td style="padding: 10px; text-align: left; border: 1px solid #ddd;">
        ${gerador.nomeTitular}
        <div style="font-size: 10px; color: ${PDF_COLORS.gray};">${gerador.documento}</div>
      </td>
      <td style="padding: 10px; text-align: left; border: 1px solid #ddd;">${gerador.cidade}/${gerador.estado}</td>
      <td style="padding: 10px; text-align: left; border: 1px solid #ddd;">${gerador.concessionaria}</td>
      <td style="padding: 10px; text-align: right; border: 1px solid #ddd;">${gerador.potenciaInstalada.toFixed(2)}</td>
      <td style="padding: 10px; text-align: right; border: 1px solid #ddd;">${gerador.geracaoEstimada.toFixed(2)}</td>
      <td style="padding: 10px; text-align: right; border: 1px solid #ddd;">
        ${gerador.custoInvestimento.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
      </td>
      <td style="padding: 10px; text-align: right; border: 1px solid #ddd; ${gerador.payback && gerador.payback < 6 ? `color: ${PDF_COLORS.green};` : ''}">
        ${gerador.payback ? gerador.payback.toFixed(1) : 'N/A'}
      </td>
    `;
    
    tbody.appendChild(row);
  });
  
  table.appendChild(tbody);
  element.appendChild(table);
  
  // Adiciona o elemento ao DOM temporariamente
  document.body.appendChild(element);
  
  // Exporta para PDF e depois remove o elemento
  exportToPDF('pdf-temp-geradores', 'relatorio-geradores', 'landscape', 'Relatório de Geradores');
  
  // Remove após um pequeno delay para garantir que o PDF foi gerado
  setTimeout(() => {
    document.body.removeChild(element);
  }, 500);
};

// Função para gerar PDF de clientes
export const generateClientesPDF = (clientes: Cliente[], geradores: Record<string, Gerador>) => {
  // Cria um elemento temporário para renderizar o conteúdo
  const element = document.createElement('div');
  element.id = 'pdf-temp-clientes';
  element.style.width = '800px';
  element.style.padding = '20px';
  element.style.backgroundColor = '#ffffff';
  element.style.fontFamily = 'Arial, sans-serif';
  
  // Adiciona título e estilo
  const header = document.createElement('div');
  header.innerHTML = `
    <h1 style="font-size: 24px; color: ${PDF_COLORS.blue}; margin-bottom: 10px; text-align: center;">
      Relatório de Clientes
    </h1>
    <p style="color: ${PDF_COLORS.gray}; margin-bottom: 20px; text-align: center;">
      Total de ${clientes.length} clientes cadastrados
    </p>
  `;
  element.appendChild(header);

  // Cria elementos para estatísticas
  const estatisticas = document.createElement('div');
  estatisticas.style.display = 'flex';
  estatisticas.style.justifyContent = 'space-between';
  estatisticas.style.marginBottom = '20px';
  
  // Cálculo de estatísticas
  const consumoTotal = clientes.reduce((sum, c) => sum + (c.consumoMedio || 0), 0);
  const gastoTotal = clientes.reduce((sum, c) => sum + c.gastoEnergia, 0);
  const economiaTotal = clientes.reduce((sum, c) => sum + (c.economiaEstimada || 0), 0);
  
  // Lista de estatísticas
  const estatisticasItems = [
    { label: 'Consumo Total', value: `${consumoTotal.toFixed(2)} kWh/mês`, color: PDF_COLORS.blue },
    { label: 'Gasto Total', value: `R$ ${gastoTotal.toLocaleString('pt-BR', {minimumFractionDigits: 2})}/mês`, color: PDF_COLORS.text },
    { label: 'Economia Gerada', value: `R$ ${economiaTotal.toLocaleString('pt-BR', {minimumFractionDigits: 2})}/mês`, color: PDF_COLORS.green },
  ];
  
  estatisticasItems.forEach(item => {
    const estatisticaItem = document.createElement('div');
    estatisticaItem.style.padding = '10px';
    estatisticaItem.style.backgroundColor = PDF_COLORS.lightGray;
    estatisticaItem.style.borderRadius = '8px';
    estatisticaItem.style.textAlign = 'center';
    estatisticaItem.style.width = '32%';
    
    estatisticaItem.innerHTML = `
      <h3 style="margin: 0 0 5px 0; color: ${item.color}; font-size: 14px;">${item.label}</h3>
      <p style="margin: 0; font-size: 18px; font-weight: bold;">${item.value}</p>
    `;
    
    estatisticas.appendChild(estatisticaItem);
  });
  
  element.appendChild(estatisticas);
  
  // Tabela de clientes
  const table = document.createElement('table');
  table.style.width = '100%';
  table.style.borderCollapse = 'collapse';
  table.style.marginTop = '20px';
  
  // Cabeçalho da tabela
  const thead = document.createElement('thead');
  thead.innerHTML = `
    <tr style="background-color: ${PDF_COLORS.blue}; color: white;">
      <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Nome do Cliente</th>
      <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Localização</th>
      <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Concessionária</th>
      <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Gerador</th>
      <th style="padding: 10px; text-align: right; border: 1px solid #ddd;">Consumo (kWh)</th>
      <th style="padding: 10px; text-align: right; border: 1px solid #ddd;">Gasto (R$)</th>
      <th style="padding: 10px; text-align: right; border: 1px solid #ddd;">Economia</th>
    </tr>
  `;
  table.appendChild(thead);
  
  // Corpo da tabela
  const tbody = document.createElement('tbody');
  clientes.forEach((cliente, index) => {
    const row = document.createElement('tr');
    row.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb';
    
    row.innerHTML = `
      <td style="padding: 10px; text-align: left; border: 1px solid #ddd;">
        ${cliente.nome}
        <div style="font-size: 10px; color: ${PDF_COLORS.gray};">${cliente.documento}</div>
      </td>
      <td style="padding: 10px; text-align: left; border: 1px solid #ddd;">${cliente.cidade}/${cliente.estado}</td>
      <td style="padding: 10px; text-align: left; border: 1px solid #ddd;">${cliente.concessionaria}</td>
      <td style="padding: 10px; text-align: left; border: 1px solid #ddd;">
        ${geradores[cliente.geradorId]?.nomeTitular || "N/A"}
      </td>
      <td style="padding: 10px; text-align: right; border: 1px solid #ddd;">
        ${cliente.consumoMedio?.toFixed(2) || "0.00"}
      </td>
      <td style="padding: 10px; text-align: right; border: 1px solid #ddd;">
        R$ ${cliente.gastoEnergia.toFixed(2)}
      </td>
      <td style="padding: 10px; text-align: right; border: 1px solid #ddd;">
        <div style="color: ${PDF_COLORS.green}; font-weight: bold;">
          ${cliente.percentualEconomia?.toFixed(1) || "0.0"}%
        </div>
        <div style="font-size: 10px;">
          R$ ${cliente.economiaEstimada?.toFixed(2) || "0.00"}/mês
        </div>
      </td>
    `;
    
    tbody.appendChild(row);
  });
  
  table.appendChild(tbody);
  element.appendChild(table);
  
  // Adiciona o elemento ao DOM temporariamente
  document.body.appendChild(element);
  
  // Exporta para PDF e depois remove o elemento
  exportToPDF('pdf-temp-clientes', 'relatorio-clientes', 'landscape', 'Relatório de Clientes');
  
  // Remove após um pequeno delay para garantir que o PDF foi gerado
  setTimeout(() => {
    document.body.removeChild(element);
  }, 500);
};

// Função para gerar PDF financeiro
export const generateFinanceiroPDF = (resumoFinanceiro: any) => {
  // Cria um elemento temporário para renderizar o conteúdo
  const element = document.createElement('div');
  element.id = 'pdf-temp-financeiro';
  element.style.width = '800px';
  element.style.padding = '20px';
  element.style.backgroundColor = '#ffffff';
  element.style.fontFamily = 'Arial, sans-serif';
  
  // Adiciona título e estilo
  const header = document.createElement('div');
  header.innerHTML = `
    <h1 style="font-size: 24px; color: ${PDF_COLORS.yellow}; margin-bottom: 10px; text-align: center;">
      Relatório Financeiro
    </h1>
    <p style="color: ${PDF_COLORS.gray}; margin-bottom: 20px; text-align: center;">
      Resumo financeiro consolidado
    </p>
  `;
  element.appendChild(header);
  
  // Cria elementos para estatísticas principais
  const mainStats = document.createElement('div');
  mainStats.style.display = 'flex';
  mainStats.style.justifyContent = 'space-between';
  mainStats.style.marginBottom = '30px';
  
  // Estatísticas principais
  const mainStatsItems = [
    { 
      label: 'Total de Clientes', 
      value: resumoFinanceiro.totalClientes,
      secondaryLabel: 'Consumo Total',
      secondaryValue: `${resumoFinanceiro.totalConsumo.toFixed(2)} kWh/mês`,
      color: PDF_COLORS.blue 
    },
    { 
      label: 'Uso do Gerador', 
      value: `${resumoFinanceiro.percentualUsoGerador.toFixed(1)}%`,
      secondaryLabel: 'Economia Total',
      secondaryValue: `R$ ${resumoFinanceiro.totalEconomia.toLocaleString('pt-BR', {minimumFractionDigits: 2})}/mês`,
      color: PDF_COLORS.green 
    },
    { 
      label: 'Receita Total', 
      value: `R$ ${resumoFinanceiro.receitaTotal.toLocaleString('pt-BR', {minimumFractionDigits: 2})}/mês`,
      secondaryLabel: 'Gasto Médio por Cliente',
      secondaryValue: `R$ ${resumoFinanceiro.gastoMedioClientes.toLocaleString('pt-BR', {minimumFractionDigits: 2})}/mês`,
      color: PDF_COLORS.yellow 
    },
  ];
  
  mainStatsItems.forEach(item => {
    const statItem = document.createElement('div');
    statItem.style.padding = '20px';
    statItem.style.backgroundColor = PDF_COLORS.lightGray;
    statItem.style.borderRadius = '10px';
    statItem.style.textAlign = 'center';
    statItem.style.width = '32%';
    statItem.style.boxShadow = '0 4px 6px rgba(0,0,0,0.05)';
    
    statItem.innerHTML = `
      <h3 style="margin: 0 0 5px 0; color: ${item.color}; font-size: 14px;">${item.label}</h3>
      <p style="margin: 0 0 15px 0; font-size: 26px; font-weight: bold;">${item.value}</p>
      <div style="height: 1px; background-color: #e5e7eb; margin: 0 20px 15px 20px;"></div>
      <h4 style="margin: 0 0 5px 0; color: ${PDF_COLORS.gray}; font-size: 12px;">${item.secondaryLabel}</h4>
      <p style="margin: 0; font-size: 14px; font-weight: bold;">${item.secondaryValue}</p>
    `;
    
    mainStats.appendChild(statItem);
  });
  
  element.appendChild(mainStats);
  
  // Informações adicionais
  const additionalInfo = document.createElement('div');
  additionalInfo.style.backgroundColor = PDF_COLORS.lightGray;
  additionalInfo.style.borderRadius = '10px';
  additionalInfo.style.padding = '20px';
  additionalInfo.style.marginBottom = '20px';
  
  additionalInfo.innerHTML = `
    <h3 style="margin: 0 0 15px 0; color: ${PDF_COLORS.text}; font-size: 16px; text-align: center;">
      Indicadores Financeiros
    </h3>
    
    <div style="display: flex; justify-content: space-between; margin-bottom: 20px;">
      <div style="width: 48%;">
        <h4 style="margin: 0 0 10px 0; color: ${PDF_COLORS.gray}; font-size: 14px;">
          Percentual de Uso dos Geradores
        </h4>
        <div style="width: 100%; height: 20px; background-color: #e5e7eb; border-radius: 10px; overflow: hidden;">
          <div style="width: ${resumoFinanceiro.percentualUsoGerador}%; height: 100%; background-color: ${PDF_COLORS.green};"></div>
        </div>
        <div style="display: flex; justify-content: space-between; margin-top: 5px;">
          <span style="font-size: 12px; color: ${PDF_COLORS.gray};">0%</span>
          <span style="font-size: 12px; color: ${PDF_COLORS.gray};">100%</span>
        </div>
      </div>
      
      <div style="width: 48%;">
        <h4 style="margin: 0 0 10px 0; color: ${PDF_COLORS.gray}; font-size: 14px;">
          Economia Média por Cliente
        </h4>
        <div style="display: flex; align-items: center;">
          <div style="width: 60px; height: 60px; border-radius: 50%; background-color: ${PDF_COLORS.blue}; display: flex; justify-content: center; align-items: center; color: white; font-weight: bold; font-size: 18px; margin-right: 15px;">
            ${(resumoFinanceiro.totalEconomia / resumoFinanceiro.totalClientes).toFixed(0)}
          </div>
          <div>
            <p style="margin: 0; font-size: 14px; font-weight: bold;">
              R$ ${(resumoFinanceiro.totalEconomia / resumoFinanceiro.totalClientes).toLocaleString('pt-BR', {minimumFractionDigits: 2})}/mês
            </p>
            <p style="margin: 0; font-size: 12px; color: ${PDF_COLORS.gray};">
              por cliente
            </p>
          </div>
        </div>
      </div>
    </div>
    
    <div style="background-color: white; border-radius: 8px; padding: 15px; box-shadow: 0 2px 4px rgba(0,0,0,0.05);">
      <h4 style="margin: 0 0 10px 0; color: ${PDF_COLORS.yellow}; font-size: 14px; text-align: center;">
        Resumo Financeiro
      </h4>
      <table style="width: 100%; border-collapse: collapse;">
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; color: ${PDF_COLORS.gray};">Receita Total Mensal:</td>
          <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; text-align: right; font-weight: bold;">
            R$ ${resumoFinanceiro.receitaTotal.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
          </td>
        </tr>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; color: ${PDF_COLORS.gray};">Projeção Anual:</td>
          <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; text-align: right; font-weight: bold;">
            R$ ${(resumoFinanceiro.receitaTotal * 12).toLocaleString('pt-BR', {minimumFractionDigits: 2})}
          </td>
        </tr>
        <tr>
          <td style="padding: 8px; color: ${PDF_COLORS.gray};">Valor Médio por Cliente:</td>
          <td style="padding: 8px; text-align: right; font-weight: bold;">
            R$ ${(resumoFinanceiro.receitaTotal / resumoFinanceiro.totalClientes).toLocaleString('pt-BR', {minimumFractionDigits: 2})}
          </td>
        </tr>
      </table>
    </div>
  `;
  
  element.appendChild(additionalInfo);
  
  // Rodapé com informações adicionais
  const footer = document.createElement('div');
  footer.style.padding = '15px';
  footer.style.backgroundColor = PDF_COLORS.text;
  footer.style.color = 'white';
  footer.style.borderRadius = '8px';
  footer.style.textAlign = 'center';
  
  const currentDate = new Date().toLocaleDateString('pt-BR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  footer.innerHTML = `
    <p style="margin: 0; font-size: 12px;">
      Relatório gerado em ${currentDate}
    </p>
    <p style="margin: 5px 0 0 0; font-size: 10px; color: ${PDF_COLORS.gray};">
      Este relatório apresenta uma visão consolidada dos indicadores financeiros do sistema.
    </p>
  `;
  
  element.appendChild(footer);
  
  // Adiciona o elemento ao DOM temporariamente
  document.body.appendChild(element);
  
  // Exporta para PDF e depois remove o elemento
  exportToPDF('pdf-temp-financeiro', 'relatorio-financeiro', 'portrait', 'Relatório Financeiro');
  
  // Remove após um pequeno delay para garantir que o PDF foi gerado
  setTimeout(() => {
    document.body.removeChild(element);
  }, 500);
};
